import React from 'react';

/**
 * Skeleton Component Library
 *
 * Provides reusable skeleton loaders for loading states throughout the app.
 * Uses Tailwind's animate-pulse for smooth loading animations.
 */

interface SkeletonProps {
  className?: string;
}

/**
 * Base skeleton element with pulse animation
 */
export const Skeleton: React.FC<SkeletonProps> = ({ className = '' }) => {
  return (
    <div
      className={`animate-pulse bg-slate-800 rounded ${className}`}
      role="status"
      aria-label="Loading..."
    />
  );
};

/**
 * Skeleton for text lines
 */
interface SkeletonTextProps {
  lines?: number;
  className?: string;
  lastLineWidth?: string;
}

export const SkeletonText: React.FC<SkeletonTextProps> = ({
  lines = 3,
  className = '',
  lastLineWidth = 'w-2/3',
}) => {
  return (
    <div className={`space-y-3 ${className}`} role="status" aria-label="Loading content...">
      {Array.from({ length: lines }).map((_, index) => (
        <div
          key={index}
          className={`h-4 bg-slate-800 rounded animate-pulse ${
            index === lines - 1 ? lastLineWidth : 'w-full'
          }`}
        />
      ))}
      <span className="sr-only">Loading...</span>
    </div>
  );
};

/**
 * Skeleton for avatar/profile images
 */
interface SkeletonAvatarProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

export const SkeletonAvatar: React.FC<SkeletonAvatarProps> = ({
  size = 'md',
  className = '',
}) => {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16',
  };

  return (
    <div
      className={`${sizeClasses[size]} rounded-full bg-slate-800 animate-pulse ${className}`}
      role="status"
      aria-label="Loading avatar..."
    />
  );
};

/**
 * Skeleton for cards (stat cards, info cards, etc.)
 */
interface SkeletonCardProps {
  className?: string;
  showHeader?: boolean;
  lines?: number;
}

export const SkeletonCard: React.FC<SkeletonCardProps> = ({
  className = '',
  showHeader = true,
  lines = 3,
}) => {
  return (
    <div
      className={`bg-st-card border border-slate-800 rounded-lg p-6 ${className}`}
      role="status"
      aria-label="Loading card..."
    >
      <div className="animate-pulse space-y-4">
        {showHeader && (
          <div className="flex items-center justify-between">
            <div className="h-4 w-24 bg-slate-800 rounded" />
            <div className="h-4 w-4 bg-slate-800 rounded" />
          </div>
        )}
        <div className="h-8 w-20 bg-slate-800 rounded" />
        {lines > 0 && (
          <div className="space-y-2">
            {Array.from({ length: lines }).map((_, i) => (
              <div
                key={i}
                className={`h-3 bg-slate-800 rounded ${i === lines - 1 ? 'w-3/4' : 'w-full'}`}
              />
            ))}
          </div>
        )}
      </div>
      <span className="sr-only">Loading...</span>
    </div>
  );
};

/**
 * Skeleton for stat/KPI cards
 */
interface SkeletonStatCardProps {
  className?: string;
}

export const SkeletonStatCard: React.FC<SkeletonStatCardProps> = ({ className = '' }) => {
  return (
    <div
      className={`bg-st-card border border-slate-800 rounded-lg p-4 ${className}`}
      role="status"
      aria-label="Loading stat..."
    >
      <div className="animate-pulse space-y-2">
        <div className="h-3 w-20 bg-slate-800 rounded" />
        <div className="h-7 w-16 bg-slate-800 rounded" />
      </div>
      <span className="sr-only">Loading...</span>
    </div>
  );
};

/**
 * Skeleton for table rows
 */
interface SkeletonTableProps {
  rows?: number;
  columns?: number;
  className?: string;
  showHeader?: boolean;
}

export const SkeletonTable: React.FC<SkeletonTableProps> = ({
  rows = 5,
  columns = 5,
  className = '',
  showHeader = true,
}) => {
  return (
    <div className={`w-full ${className}`} role="status" aria-label="Loading table...">
      <div className="animate-pulse">
        {/* Table Header */}
        {showHeader && (
          <div className="flex gap-4 p-4 border-b border-slate-800">
            {Array.from({ length: columns }).map((_, i) => (
              <div
                key={`header-${i}`}
                className="h-4 bg-slate-800 rounded flex-1"
              />
            ))}
          </div>
        )}
        {/* Table Rows */}
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div
            key={rowIndex}
            className="flex gap-4 p-4 border-b border-slate-800/50"
          >
            {Array.from({ length: columns }).map((_, colIndex) => (
              <div
                key={`cell-${rowIndex}-${colIndex}`}
                className={`h-4 bg-slate-800 rounded flex-1 ${
                  colIndex === 0 ? 'max-w-[150px]' : ''
                }`}
              />
            ))}
          </div>
        ))}
      </div>
      <span className="sr-only">Loading...</span>
    </div>
  );
};

/**
 * Skeleton for charts
 */
interface SkeletonChartProps {
  height?: number | string;
  className?: string;
  type?: 'line' | 'bar' | 'candlestick';
}

export const SkeletonChart: React.FC<SkeletonChartProps> = ({
  height = 300,
  className = '',
  type = 'line',
}) => {
  return (
    <div
      className={`bg-st-card border border-slate-800 rounded-lg p-4 ${className}`}
      style={{ height: typeof height === 'number' ? `${height}px` : height }}
      role="status"
      aria-label="Loading chart..."
    >
      <div className="animate-pulse h-full flex flex-col">
        {/* Chart Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="h-5 w-32 bg-slate-800 rounded" />
          <div className="flex gap-2">
            <div className="h-6 w-16 bg-slate-800 rounded" />
            <div className="h-6 w-16 bg-slate-800 rounded" />
          </div>
        </div>

        {/* Chart Area */}
        <div className="flex-1 flex items-end gap-1 pb-4">
          {type === 'bar' || type === 'candlestick' ? (
            // Bar/Candlestick chart skeleton
            Array.from({ length: 20 }).map((_, i) => (
              <div
                key={i}
                className="flex-1 bg-slate-800 rounded-t"
                style={{ height: `${Math.random() * 60 + 20}%` }}
              />
            ))
          ) : (
            // Line chart skeleton
            <div className="w-full h-full relative">
              <svg viewBox="0 0 400 150" className="w-full h-full">
                <path
                  d="M0,100 Q50,80 100,90 T200,60 T300,80 T400,40"
                  fill="none"
                  stroke="rgb(30 41 59)"
                  strokeWidth="3"
                  className="animate-pulse"
                />
              </svg>
            </div>
          )}
        </div>

        {/* X-axis labels */}
        <div className="flex justify-between mt-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-3 w-12 bg-slate-800 rounded" />
          ))}
        </div>
      </div>
      <span className="sr-only">Loading chart...</span>
    </div>
  );
};

/**
 * Skeleton for list items
 */
interface SkeletonListItemProps {
  showAvatar?: boolean;
  showAction?: boolean;
  className?: string;
}

export const SkeletonListItem: React.FC<SkeletonListItemProps> = ({
  showAvatar = true,
  showAction = true,
  className = '',
}) => {
  return (
    <div
      className={`flex items-center gap-4 p-4 ${className}`}
      role="status"
      aria-label="Loading item..."
    >
      <div className="animate-pulse flex items-center gap-4 w-full">
        {showAvatar && <div className="w-10 h-10 rounded-full bg-slate-800 shrink-0" />}
        <div className="flex-1 space-y-2">
          <div className="h-4 w-3/4 bg-slate-800 rounded" />
          <div className="h-3 w-1/2 bg-slate-800 rounded" />
        </div>
        {showAction && <div className="w-8 h-8 bg-slate-800 rounded shrink-0" />}
      </div>
      <span className="sr-only">Loading...</span>
    </div>
  );
};

/**
 * Skeleton for chat messages
 */
interface SkeletonMessageProps {
  isUser?: boolean;
  className?: string;
}

export const SkeletonMessage: React.FC<SkeletonMessageProps> = ({
  isUser = false,
  className = '',
}) => {
  return (
    <div
      className={`flex items-start gap-4 ${isUser ? 'flex-row-reverse' : ''} ${className}`}
      role="status"
      aria-label="Loading message..."
    >
      <div className="animate-pulse flex items-start gap-4 w-full">
        <div className="w-10 h-10 rounded-full bg-slate-800 shrink-0" />
        <div
          className={`flex-1 max-w-[80%] space-y-2 p-4 rounded-2xl ${
            isUser ? 'bg-st-primary/10 ml-auto' : 'bg-slate-800/50'
          }`}
        >
          <div className="h-4 w-full bg-slate-700 rounded" />
          <div className="h-4 w-5/6 bg-slate-700 rounded" />
          <div className="h-4 w-2/3 bg-slate-700 rounded" />
        </div>
      </div>
      <span className="sr-only">Loading...</span>
    </div>
  );
};

/**
 * Skeleton for page header
 */
interface SkeletonPageHeaderProps {
  className?: string;
}

export const SkeletonPageHeader: React.FC<SkeletonPageHeaderProps> = ({ className = '' }) => {
  return (
    <div className={`space-y-4 ${className}`} role="status" aria-label="Loading page...">
      <div className="animate-pulse space-y-4">
        <div className="h-6 w-24 bg-slate-800 rounded-full" />
        <div className="h-10 w-64 bg-slate-800 rounded" />
        <div className="h-4 w-96 max-w-full bg-slate-800 rounded" />
      </div>
      <span className="sr-only">Loading...</span>
    </div>
  );
};

/**
 * Full page skeleton loader
 */
interface SkeletonPageProps {
  showHeader?: boolean;
  showCards?: number;
  showChart?: boolean;
  showTable?: boolean;
  className?: string;
}

export const SkeletonPage: React.FC<SkeletonPageProps> = ({
  showHeader = true,
  showCards = 4,
  showChart = false,
  showTable = false,
  className = '',
}) => {
  return (
    <div className={`space-y-6 ${className}`} role="status" aria-label="Loading page...">
      {showHeader && <SkeletonPageHeader />}

      {showCards > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: showCards }).map((_, i) => (
            <SkeletonStatCard key={i} />
          ))}
        </div>
      )}

      {showChart && <SkeletonChart height={350} />}

      {showTable && <SkeletonTable rows={5} columns={5} />}

      <span className="sr-only">Loading...</span>
    </div>
  );
};
